import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAvailableTags } from '../../../../hooks/queries/useTasks';
import { TaskItem } from '../../../../types/topic';
// Migrated from useTaskState to taskUIStore for unified state management
import { useTaskUIStore } from '../../../../store/taskUIStore';
import { useTaskHandlers } from '../hooks/useTaskHandlers';
import { logger } from '../../../../utils/logger';
import { cn } from '../../../../lib/utils';
import { useColumnActions } from './useColumnActions';
import { useDndColumn } from './useDndColumn';
import { useTaskMove } from './useTaskMove';
import { useDragOverlay } from './useDragOverlay';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { UserFilter } from '../../../dashboard/types/dashboardTypes';
import { TaskCard } from '../TaskCard';
import { EmptyTaskState } from '../EmptyTaskState';
import { TaskForm } from '../TaskForm';
import { Types } from './taskTypes';
import { ColumnMenu } from '../ColumnMenu';
import { getStatusColor, extractBaseColor } from '../../../../utils/colorUtils';
import { COLUMN_TO_BORDER_COLOR_MAP } from '../../../../constants/colorConstants';
import { getColumnTextColorClass } from '../utils/columnUtils';
import { useToastUIStore } from '../../../../store/toastUIStore';
import { AlertCircle, Loader2, Mic, Plus } from 'lucide-react';
import { useTaskColumns } from '../../../../hooks/queries/useTaskColumns';
import { TaskBoardLayout } from '../layout/TaskBoardLayout';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  rectIntersection,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';

// Helper to get text color from column color moved to ../utils/columnUtils.ts

// Simple draggable task card component for deadline-sorted mode
// This only supports cross-column dragging, not within-column reordering
interface SortedDraggableTaskCardProps {
  task: TaskItem;
  projectId: string;
  index: number;
  itemToDelete: string | null;
  isFirst: boolean;
  isLast: boolean;
  onDelete: (id: string) => void;
  onDeleteClick: (id: string) => void;
  onCancelDelete: () => void;
  onUpdateTask: (id: string, updates: Partial<TaskItem>) => void;
  hasCollaborators?: boolean;
}

const SortedDraggableTaskCard: React.FC<SortedDraggableTaskCardProps> = ({
  task,
  projectId,
  index,
  itemToDelete,
  isFirst,
  isLast,
  onDelete,
  onDeleteClick,
  onCancelDelete,
  onUpdateTask,
  hasCollaborators,
}) => {
  // State for editing task
  const [isEditing, setIsEditing] = useState(false);

  // Set up drag attributes with dnd-kit
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: task.id,
    data: {
      task,
      type: 'task',
    },
    disabled: isEditing, // Can't drag while editing
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ cursor: 'grab' }}
      className="relative z-[5]"
    >
      <TaskCard
        task={{
          ...task,
          _noDragHandling: true, // Prevent TaskCard from handling its own dragging
        }}
        projectId={projectId}
        index={index}
        isFirst={isFirst}
        isLast={isLast}
        itemToDelete={itemToDelete}
        onDelete={onDelete}
        onDeleteClick={onDeleteClick}
        onCancelDelete={onCancelDelete}
        onUpdateTask={onUpdateTask}
        hasCollaborators={hasCollaborators}
        onEditingStateChange={setIsEditing}
        customDragHandlers={true} // Use our custom drag handlers
      />
    </div>
  );
};

interface DeadlineSortedTaskColumnProps {
  column: {
    id: string;
    status: string;
    name: string;
    color?: string;
  };
  items: TaskItem[];
  topicId: string;
  projectId: string;
  isRecording: boolean;
  isCreating: boolean;
  activeColumn: TaskItem['status'];
  title: string;
  description: string;
  tags: string[];
  deadline: Date | null;
  assignedTo: string | null;
  showDescription: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  isEditingColumnName: string | null;
  totalTaskCount: number;
  hasCollaborators?: boolean;
  className?: string;
  // Filter props
  showUnscheduledOnly?: boolean;
  showUnassignedOnly?: boolean;
  userFilters?: UserFilter[];
  onVoiceClick: () => void;
  onAddClick: () => void;
  onTitleChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onTagsChange: (value: string[]) => void;
  onDeadlineChange: (value: Date | null) => void;
  onAssigneeChange?: (value: string | null) => void;
  onSubmit: () => void;
  onCancel: () => void;
  onEditColumnNameClick: (columnId: string) => void;
  onEditColumnName: (columnId: string, name: string) => void;
  onDeleteColumnClick: (columnId: string) => void;
  onColumnColorSelect: (columnId: string, color: string) => void;
  onAddColumnAfter: (columnId: string) => void;
  onMoveColumnLeft: (columnId: string) => void;
  onMoveColumnRight: (columnId: string) => void;
  isFirstColumn: boolean;
  isLastColumn: boolean;
}

// Specialized column component for deadline-sorted tasks
const DeadlineSortedTaskColumn: React.FC<DeadlineSortedTaskColumnProps> = ({
  column,
  items,
  topicId,
  projectId,
  isRecording,
  isCreating,
  activeColumn,
  title,
  description,
  tags,
  deadline,
  assignedTo,
  showDescription,
  inputRef,
  isEditingColumnName,
  totalTaskCount,
  hasCollaborators = false,
  className,
  // Filter props
  showUnscheduledOnly,
  showUnassignedOnly,
  userFilters,
  onVoiceClick,
  onAddClick,
  onTitleChange,
  onDescriptionChange,
  onTagsChange,
  onDeadlineChange,
  onAssigneeChange,
  onSubmit,
  onCancel,
  onEditColumnNameClick,
  onEditColumnName,
  onDeleteColumnClick,
  onColumnColorSelect,
  onAddColumnAfter,
  onMoveColumnLeft,
  onMoveColumnRight,
  isFirstColumn,
  isLastColumn,
}) => {
  // State for column name editing
  const [editingName, setEditingName] = useState(column.name);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Focus input when editing column name
  useEffect(() => {
    if (isEditingColumnName === column.id && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingColumnName, column.id]);

  // Handle column name edit with better optimistic UI
  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Only update if the name actually changed and isn't empty
    const newName = editingName.trim();
    if (newName !== column.name && newName !== '') {
      // Send the update to the database
      onEditColumnName(column.id, newName);
    } else if (newName === '') {
      // Reset to original name if empty
      setEditingName(column.name);
      onEditColumnName(column.id, column.name);
    } else {
      // No change, just exit edit mode
      onEditColumnName(column.id, column.name);
    }
  };

  // Reset editing name when column changes
  useEffect(() => {
    setEditingName(column.name);
  }, [column.name]);

  // Get all available tags for autocomplete
  const availableTags = useAvailableTags(topicId);

  // Get columns using React Query
  const { data: columns = [] } = useTaskColumns(topicId);

  // Set up droppable for the column to allow tasks to be dropped here
  const { setNodeRef } = useDroppable({
    id: column.status,
    data: {
      type: Types.COLUMN,
      status: column.status,
      id: column.id,
      name: column.name,
    },
  });

  return (
    <div
      className={cn('flex flex-col h-full relative z-10', className)}
      style={{
        contain: 'layout', // Isolate layout calculations
        willChange: 'transform', // Hint for browser optimization
      }}
    >
      {/* Column header */}
      <div className="mb-3 p-2 flex items-center justify-between flex-shrink-0 rounded-md relative z-[100]">
        <div className="flex items-center gap-2 max-w-[75%]">
          {isEditingColumnName === column.id ? (
            <form onSubmit={handleNameSubmit} className="flex-1 flex items-center">
              <input
                ref={nameInputRef}
                type="text"
                value={editingName}
                onChange={e => setEditingName(e.target.value)}
                onBlur={handleNameSubmit}
                className="w-full bg-transparent font-bold font-sora focus:outline-none"
                style={{
                  // Match input color to the column color
                  color: (() => {
                    if (!column.color) return 'text-gray-900';

                    const baseColor = extractBaseColor(column.color);
                    if (baseColor) {
                      const borderColor = COLUMN_TO_BORDER_COLOR_MAP[baseColor];
                      if (borderColor) {
                        return borderColor.replace('border-', 'text-');
                      }
                    }

                    // Try direct mapping
                    const directColor = COLUMN_TO_BORDER_COLOR_MAP[column.color];
                    if (directColor) {
                      return directColor.replace('border-', 'text-');
                    }

                    return 'text-gray-900';
                  })(),
                }}
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Escape') {
                    setEditingName(column.name);
                    onEditColumnName(column.id, column.name);
                  }
                }}
              />
              <span className="inline-flex items-center justify-center h-6 min-w-[24px] rounded-full bg-white text-gray-600 text-xs font-medium px-2 flex-shrink-0 ml-2">
                {items.length}
              </span>
            </form>
          ) : (
            <h3
              className={`font-bold font-sora flex items-center gap-2 cursor-pointer truncate ${getColumnTextColorClass(column.color) || 'text-gray-900'}`}
              onClick={() => onEditColumnNameClick(column.id)}
              title="Click to edit column name"
            >
              {column.name}
              <span className="inline-flex items-center justify-center h-6 min-w-[24px] rounded-full bg-white text-gray-600 text-xs font-medium px-2 flex-shrink-0">
                {items.length}
              </span>
            </h3>
          )}
        </div>
        <div className="flex items-center gap-1 relative z-[101]">
          <ColumnMenu
            column={column}
            isFirst={isFirstColumn}
            isLast={isLastColumn}
            onDeleteClick={onDeleteColumnClick}
            onEditNameClick={onEditColumnNameClick}
            onColorSelect={onColumnColorSelect}
            onAddColumn={onAddColumnAfter}
            onMoveLeft={onMoveColumnLeft}
            onMoveRight={onMoveColumnRight}
          />
          <button
            onClick={onVoiceClick}
            className={cn(
              'p-1.5 rounded-lg transition-colors',
              isRecording
                ? 'bg-red-500 text-white hover:bg-red-600'
                : `bg-gray-100 ${getColumnTextColorClass(column.color)} hover:bg-gray-200`
            )}
            title={isRecording ? 'Stop recording' : 'Start voice recording'}
          >
            <Mic size={16} className={cn(isRecording && 'animate-pulse')} />
          </button>
          <button
            onClick={onAddClick}
            className={`p-1.5 rounded-lg bg-gray-100 ${getColumnTextColorClass(column.color)} hover:bg-gray-200 transition-colors`}
            title="Add task"
          >
            <Plus size={16} />
          </button>
        </div>
      </div>

      {/* Column content - droppable area */}
      <div
        ref={setNodeRef}
        data-column-status={column.status}
        data-column-id={column.id}
        className="flex-1 rounded-lg flex flex-col overflow-hidden"
      >
        <div className="relative overflow-visible h-full flex-1">
          {/* Tasks list with vertical scrolling only */}
          <div
            className="space-y-3 overflow-y-auto pb-0 pt-0 h-full relative"
            style={{ isolation: 'isolate', overflowX: 'visible' }}
          >
            {/* Always show form at the top when active */}
            {isCreating && activeColumn === column.status && (
              <div
                className={cn(
                  'bg-white shadow w-full px-4 pt-3 pb-2 rounded-[5px] border-l-[5px]',
                  column.color
                    ? (() => {
                        // Format current columns for consistent calculations - using already fetched columns
                        // Use a defensive approach in case columns data is still loading or empty
                        const formattedStatusColors =
                          columns.length > 0
                            ? columns.map(col => ({
                                status: col.status,
                                color: extractBaseColor(col.color) || 'gray',
                              }))
                            : [
                                { status: 'todo', color: 'blue' },
                                { status: 'doing', color: 'yellow' },
                                { status: 'done', color: 'green' },
                              ];

                        // Get border color directly using the getStatusColor utility
                        return getStatusColor(activeColumn, formattedStatusColors, 'border', false);
                      })()
                    : activeColumn === 'todo'
                      ? 'border-blue-500'
                      : activeColumn === 'doing'
                        ? 'border-yellow-500'
                        : activeColumn === 'done'
                          ? 'border-green-500'
                          : 'border-gray-500'
                )}
              >
                <TaskForm
                  isEditMode={false}
                  projectId={projectId}
                  title={title}
                  description={description}
                  tags={tags}
                  deadline={deadline}
                  assignedTo={assignedTo}
                  status={column.status}
                  showDescription={showDescription}
                  hasCollaborators={hasCollaborators}
                  onTitleChange={onTitleChange}
                  onDescriptionChange={onDescriptionChange}
                  onTagsChange={onTagsChange}
                  onDeadlineChange={onDeadlineChange}
                  onAssigneeChange={onAssigneeChange}
                  onSubmitNewTask={onSubmit}
                  onCancel={onCancel}
                  inputRef={inputRef}
                  availableTags={availableTags}
                />
              </div>
            )}

            {/* Draggable task cards (only for cross-column drag in sorted mode) */}
            {items.map((item, index) => (
              <SortedDraggableTaskCard
                key={item.id}
                task={item}
                projectId={projectId}
                index={index}
                isFirst={index === 0}
                isLast={index === items.length - 1}
                itemToDelete={itemToDelete}
                onDelete={onDelete}
                onDeleteClick={onDeleteClick}
                onCancelDelete={onCancelDelete}
                onUpdateTask={onUpdateTask}
                hasCollaborators={hasCollaborators}
              />
            ))}

            {/* Empty state for different filter conditions */}
            {items.length === 0 &&
              (totalTaskCount === 0 && !isCreating ? (
                // Default empty state when no tasks at all
                <div className="flex flex-col items-center justify-center text-center pt-4 pb-8">
                  <div className="mb-2">
                    <Plus size={20} className="text-gray-300" />
                  </div>
                  <p className="text-sm text-gray-300">No tasks yet</p>
                </div>
              ) : (
                // Empty state for filtered tasks
                <EmptyTaskState
                  showUnscheduledOnly={showUnscheduledOnly}
                  showUnassignedOnly={showUnassignedOnly}
                  userFilters={userFilters}
                  isMobile={false}
                />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

interface DeadlineSortedTaskBoardProps {
  topicId: string;
  projectId: string;
  tasks: TaskItem[];
  hasCollaborators?: boolean;
  showUnscheduledOnly?: boolean;
  showUnassignedOnly?: boolean;
  userFilters?: UserFilter[];
}

/**
 * DeadlineSortedTaskBoard - A specialized version of the task board for deadline sorting mode.
 * This component renders tasks sorted by deadline with no drag reordering capabilities within columns,
 * but allows dragging tasks between columns.
 */
export const DeadlineSortedTaskBoard: React.FC<DeadlineSortedTaskBoardProps> = ({
  topicId,
  projectId,
  tasks,
  hasCollaborators = false,
  showUnscheduledOnly,
  showUnassignedOnly,
  userFilters,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // State for the currently dragged task
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const activeTask = activeTaskId ? tasks.find(task => task.id === activeTaskId) : null;

  // Toast store for notifications
  const addToast = useToastUIStore(state => state.addToast);

  // Ref to track when the toast was last shown (for rate limiting)
  const lastToastTimeRef = useRef<number>(0);

  // Use shared task handlers instead of duplicating logic
  const { handleTaskCreation, handleVoiceInput, isRecording } = useTaskHandlers(topicId);

  // Task columns from React Query
  const {
    data: columns = [],
    isLoading: isColumnsLoading,
    isError: isColumnsError,
  } = useTaskColumns(topicId);

  // Drag overlay configuration using existing hook
  const dragOverlay = useDragOverlay({ activeTaskId, activeTask });

  // Task state and actions from unified taskUIStore
  const {
    isCreating,
    showDescription,
    newTaskTitle,
    newTaskDescription,
    newTaskTags,
    newTaskDeadline,
    newTaskAssignedTo,
    activeColumn,
    columnToDelete,
    recordingColumn,
    isEditingColumnName,
    setActiveColumn,
    setIsCreating,
    setShowDescription,
    setNewTaskTitle,
    setNewTaskDescription,
    setNewTaskTags,
    setNewTaskDeadline,
    setNewTaskAssignedTo,
    setColumnToDelete,
    setIsEditingColumnName,
  } = useTaskUIStore();

  // Set the active topic ID in the store for task creation
  useEffect(() => {
    useTaskUIStore.setState({ activeTopicId: topicId });
  }, [topicId]);

  // Get task move hook
  const { moveTaskBetweenColumns } = useTaskMove(topicId, tasks, columns);

  // Column management hooks
  const {
    handleAddColumnAfter,
    handleEditColumnName,
    handleDeleteColumn,
    handleColumnColorChange,
    handleMoveColumnLeft,
    handleMoveColumnRight,
  } = useColumnActions({
    topicId,
    columns,
    setIsEditingColumnName,
    setColumnToDelete,
  });

  // Handle task form submission
  const handleSubmit = useCallback(
    async (columnId: TaskItem['status']) => {
      try {
        // Get the current form data from taskUIStore
        const currentTitle = newTaskTitle;

        // Validate title exists
        if (!currentTitle?.trim()) {
          logger.error('Cannot submit task with empty title from component state');
          return;
        }

        // Use shared task creation handler with explicit data
        await handleTaskCreation(columnId, {
          title: currentTitle,
          description: newTaskDescription,
          tags: newTaskTags,
          deadline: newTaskDeadline,
          assignedTo: newTaskAssignedTo,
        });

        // Set focus on input after successful task creation
        setTimeout(() => inputRef.current?.focus(), 0);
      } catch (error) {
        // Error handling is now managed by the shared handler
        logger.error('Unexpected error in deadline sorted form submission:', error);
      }
    },
    [
      handleTaskCreation,
      newTaskTitle,
      newTaskDescription,
      newTaskTags,
      newTaskDeadline,
      newTaskAssignedTo,
    ]
  );

  // Configure DND sensors with more distance for activation
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10, // Require more movement before activating in deadline sorted mode
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag start
  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const { active } = event;

      // Get the task ID from the event
      const taskId = active.id.toString();

      // Set the active task ID
      setActiveTaskId(taskId);

      // Show toast notification about dragging in sorted mode (rate limited to once every 5 minutes)
      const now = Date.now();
      const fiveMinutesInMs = 5 * 60 * 1000;

      if (now - lastToastTimeRef.current > fiveMinutesInMs) {
        addToast({
          type: 'info',
          message:
            'Tasks are already sorted by deadline. You can only drag and drop between columns.',
          duration: 3000,
        });

        // Update the last toast time
        lastToastTimeRef.current = now;
      }
    },
    [setActiveTaskId, addToast]
  );

  // Handle drag end
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      // Always clear active task on drop to reset opacity
      setActiveTaskId(null);

      // If there's no drop target, do nothing further
      if (!over) return;

      // Get the task ID and over column from the event
      const taskId = active.id.toString();
      const task = tasks.find(t => t.id === taskId);

      // If task wasn't found, do nothing further
      if (!task) return;

      // Get the target column data
      const overStatus = over.id.toString();

      // If the task is already in this column, just exit - no reordering in deadline sort mode
      if (task.status === overStatus) {
        logger.component('Dropped in same column (no change in deadline sort mode)', {
          taskId,
          status: task.status,
        });
        return;
      }

      // Move the task to the new column with position 0 (always place at the top)
      moveTaskBetweenColumns(taskId, overStatus, 0);

      logger.component('Moving task between columns in deadline-sorted mode', {
        taskId,
        fromStatus: task.status,
        toStatus: overStatus,
      });
    },
    [tasks, moveTaskBetweenColumns, setActiveTaskId, logger]
  );

  // Filter out any legacy status tasks that don't have matching columns
  const validStatuses = useMemo(() => columns.map(col => col.status), [columns]);

  // Filter tasks by valid statuses
  const filteredTasks = useMemo(() => {
    return tasks.filter(task => validStatuses.includes(task.status));
  }, [tasks, validStatuses]);

  // Column width calculations
  const { containerRef, boardRef, columnWidth, shouldScroll, measurementComplete } =
    useDndColumn(columns);

  // Don't render anything until measurements are complete
  if (!measurementComplete || !columnWidth) {
    return <div className="h-full w-full bg-gray-50" ref={containerRef}></div>;
  }

  // Handle columns loading state
  if (isColumnsLoading && !columns.length) {
    return (
      <div className="h-full w-full bg-gray-50 flex items-center justify-center" ref={containerRef}>
        <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  // Handle columns error state
  if (isColumnsError) {
    return (
      <div
        className="h-full w-full bg-gray-50 flex flex-col items-center justify-center p-4 text-center"
        ref={containerRef}
      >
        <AlertCircle className="w-8 h-8 text-red-500 mb-2" />
        <p className="text-sm text-red-500">Failed to load task columns</p>
      </div>
    );
  }

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection} // Use simpler collision detection for column drops
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <TaskBoardLayout
          containerRef={containerRef}
          boardRef={boardRef}
          shouldScroll={shouldScroll}
          measurementComplete={measurementComplete}
          columnWidth={columnWidth}
          columns={columns}
          columnToDelete={columnToDelete}
          onDeleteColumn={handleDeleteColumn}
          onCancelDelete={() => setColumnToDelete(null)}
        >
          {/* Render all columns */}
          {columns.map(column => (
            <DeadlineSortedTaskColumn
              key={column.id}
              column={column}
              items={filteredTasks.filter(task => task.status === column.status)}
              topicId={topicId}
              className={columnWidth}
              isRecording={isRecording && recordingColumn === column.status}
              isCreating={isCreating}
              activeColumn={activeColumn}
              title={newTaskTitle}
              description={newTaskDescription}
              tags={newTaskTags}
              deadline={newTaskDeadline}
              assignedTo={newTaskAssignedTo}
              showDescription={showDescription}
              inputRef={inputRef}
              isEditingColumnName={isEditingColumnName}
              totalTaskCount={filteredTasks.length}
              projectId={projectId}
              hasCollaborators={hasCollaborators}
              // Pass filter props for empty state
              showUnscheduledOnly={showUnscheduledOnly}
              showUnassignedOnly={showUnassignedOnly}
              userFilters={userFilters}
              onVoiceClick={() => handleVoiceInput(column.status)}
              onAddClick={() => {
                setActiveColumn(column.status);
                setIsCreating(true);
                setShowDescription(false);
                setTimeout(() => inputRef.current?.focus(), 0);
              }}
              onTitleChange={setNewTaskTitle}
              onDescriptionChange={setNewTaskDescription}
              onTagsChange={setNewTaskTags}
              onDeadlineChange={setNewTaskDeadline}
              onAssigneeChange={setNewTaskAssignedTo}
              onSubmit={() => handleSubmit(column.status)}
              onCancel={() => {
                setIsCreating(false);
                setShowDescription(false);
                setNewTaskAssignedTo(null);
              }}
              onEditColumnNameClick={id => {
                setIsEditingColumnName(id);
              }}
              onEditColumnName={handleEditColumnName}
              onDeleteColumnClick={id => setColumnToDelete(id)}
              onColumnColorSelect={handleColumnColorChange}
              onAddColumnAfter={handleAddColumnAfter}
              onMoveColumnLeft={handleMoveColumnLeft}
              onMoveColumnRight={handleMoveColumnRight}
              isFirstColumn={columns.indexOf(column) === 0}
              isLastColumn={columns.indexOf(column) === columns.length - 1}
            />
          ))}
        </TaskBoardLayout>

        {/* Drag Overlay - shows a preview of the task being dragged */}
        <DragOverlay adjustScale={false} zIndex={100} dropAnimation={dragOverlay.dropAnimation}>
          {dragOverlay.renderOverlay()}
        </DragOverlay>
      </DndContext>
    </>
  );
};
